import React, { useState } from "react";
import axios from "axios";
import { ArrowUpRight } from "lucide-react";
import { contactEmail, social } from "../data/content";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fieldBase =
  "w-full bg-transparent border-b border-neutral-300 py-3 text-[15px] text-neutral-900 placeholder:text-neutral-400 focus:border-neutral-900 focus:outline-none transition-colors";

const ContactForm = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [error, setError] = useState("");

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError("Please fill in every field.");
      setStatus("error");
      return;
    }
    setStatus("sending");
    setError("");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("sent");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(detail || "Something went wrong. Please try again.");
      setStatus("error");
    }
  };

  if (status === "sent") {
    return (
      <div className="mt-10 border-t border-neutral-300 pt-10 text-center">
        <p className="font-display text-[20px] md:text-[24px]">Thank you.</p>
        <p className="mt-3 text-[15px] text-neutral-500">
          Your message has been sent — I'll be in touch soon.
        </p>
        <button
          onClick={() => setStatus("idle")}
          className="underlink mt-6 text-[12px] tracking-[0.14em] uppercase text-neutral-500 hover:text-neutral-900 transition-colors"
        >
          Send another
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="mt-10 w-full max-w-lg mx-auto text-left">
      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <label className="text-[10px] tracking-[0.22em] uppercase text-neutral-400">
            Name
          </label>
          <input
            name="name"
            value={form.name}
            onChange={onChange}
            placeholder="Your name"
            className={fieldBase}
          />
        </div>
        <div>
          <label className="text-[10px] tracking-[0.22em] uppercase text-neutral-400">
            Email
          </label>
          <input
            name="email"
            type="email"
            value={form.email}
            onChange={onChange}
            placeholder="you@email.com"
            className={fieldBase}
          />
        </div>
      </div>
      <div className="mt-6">
        <label className="text-[10px] tracking-[0.22em] uppercase text-neutral-400">
          Message
        </label>
        <textarea
          name="message"
          value={form.message}
          onChange={onChange}
          placeholder="Tell me a little about what you're working on…"
          rows={4}
          className={`${fieldBase} resize-none`}
        />
      </div>

      {status === "error" && (
        <p className="mt-4 text-[13px] text-red-600">{error}</p>
      )}

      <div className="mt-8 flex items-center justify-between">
        <button
          type="submit"
          disabled={status === "sending"}
          className="group inline-flex items-center gap-2 border border-neutral-900 px-6 py-3 text-[12px] tracking-[0.16em] uppercase text-neutral-900 hover:bg-neutral-900 hover:text-[#f6f5f2] transition-colors duration-300 disabled:opacity-50"
        >
          {status === "sending" ? "Sending…" : "Send message"}
          <ArrowUpRight
            size={16}
            strokeWidth={1.5}
            className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
          />
        </button>
        <a
          href={`mailto:${contactEmail}`}
          className="underlink text-[12px] text-neutral-400 hover:text-neutral-900 transition-colors"
        >
          {contactEmail}
        </a>
      </div>

      <div className="mt-10 pt-6 border-t border-neutral-200 flex items-center justify-center gap-6">
        {social.map((s) => (
          <a
            key={s.href}
            href={s.href}
            target="_blank"
            rel="noopener noreferrer"
            className="underlink text-[11px] tracking-[0.18em] uppercase text-neutral-500 hover:text-neutral-900 transition-colors"
          >
            {s.label}
          </a>
        ))}
      </div>
    </form>
  );
};

export default ContactForm;
